import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Play, Pause, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

/* ━━━ Screenshot Mapping ━━━ */
const SS = {
  hero: '/screenshots/screenshot-03.png',
  dashboard: '/screenshots/screenshot-02.png',
  clinical: '/screenshots/screenshot-03.png',
  soap: '/screenshots/screenshot-04.png',
  patients: '/screenshots/screenshot-05.png',
  encounterList: '/screenshots/screenshot-06.png',
  encounterReview: '/screenshots/screenshot-07.png',
  aiInsights: '/screenshots/screenshot-08.png',
  mips: '/screenshots/screenshot-09.png',
};

/* ━━━ Animation variants ━━━ */
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
};
const fadeUpHeading = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const scaleIn = {
  hidden: { opacity: 0, scale: 0.97 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } },
};

/* ━━━ Carousel data ━━━ */
const slides = [
  { eyebrow: 'Dashboard', title: 'Your day, organized.', body: 'See all 8 patients, their status, and the AI workflow stepper — everything ready before the first appointment.', img: SS.dashboard },
  { eyebrow: 'AI Patient Brief', title: 'Every patient, pre-briefed.', body: 'Before you walk in, Lumina surfaces the HbA1c trend, active medications, care gaps, and coding opportunities.', img: SS.clinical },
  { eyebrow: 'Ambient Listening', title: 'Just talk to your patient.', body: 'Lumina listens in real time. Live transcript, AI clinical suggestions, and Lens uploads — all on the right side while the chart stays open on the left.', img: SS.hero },
  { eyebrow: 'SOAP Note', title: 'Sign. Done. Next patient.', body: 'A complete SOAP note generated from the conversation. Review one section, sign, and close the encounter — in under two minutes.', img: SS.soap },
];

/* ━━━ Sticky scroll data ━━━ */
const stickyBlocks = [
  { eyebrow: 'Before the visit', h3: 'Your patients are already briefed.', body: 'Lumina generates an AI Patient Brief before the appointment — HbA1c trends, active meds, care gaps, and HCC coding opportunities. You walk in knowing exactly what matters.', pill: '4 min average prep time saved', img: SS.dashboard },
  { eyebrow: 'During the visit', h3: 'Talk to your patient. Not your computer.', body: 'The moment you tap Start Visit, Lumina listens. Speaker-labeled transcript on the right. Your patient chart on the left. AI clinical suggestions updating in real time as the conversation develops.', pill: 'Works in 60+ languages', img: SS.clinical },
  { eyebrow: 'After the visit', h3: 'The note is already written.', body: 'Stop. Review. A complete SOAP note structured from the conversation — Subjective, Objective, Assessment, Plan. Edit one line if needed. Sign. Close. Move to the next patient.', pill: 'Up to 70% less documentation time', img: SS.soap },
  { eyebrow: 'End of day', h3: 'No unsigned notes. No pajama time.', body: 'Encounter Review shows all today\'s notes with their status — Draft, Finalized, Signed. Batch-sign, fix a coding gap, or send to billing. Done before you leave the clinic.', pill: 'Zero after-hours charting', img: SS.encounterList },
];

/* ━━━ Feature card data ━━━ */
const featureCards = [
  { name: 'AI Patient Brief', desc: 'Surfaces conditions, trends, meds, and care gaps before you enter the exam room.', img: SS.clinical },
  { name: 'Ambient Listening', desc: 'Captures the full conversation. Works in 60+ languages with speaker identification.', img: SS.hero },
  { name: 'AI SOAP Notes', desc: 'Complete structured notes generated from conversation. Review, edit one line, sign.', img: SS.soap },
  { name: 'HCC & Coding Intelligence', desc: 'RAF score, coding gaps, and HCC opportunities surfaced at every encounter.', img: SS.encounterReview },
  { name: 'AI Insights & MIPS', desc: 'Clinical alerts, care gaps, MIPS measures, and medication flags tracked automatically.', img: SS.aiInsights },
  { name: 'Encounter Review', desc: 'All notes in one place. Batch sign, fix coding gaps, send to billing — without hunting.', img: SS.encounterList },
];

/* ━━━ SECTION 1: Hero ━━━ */
const HeroSection = () => {
  const mockupRef = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 180], [72, 0]);
  const rotX = useTransform(scrollY, [0, 180], [6, 0]);
  const sc = useTransform(scrollY, [0, 180], [0.97, 1]);
  const op = useTransform(scrollY, [0, 180], [0.75, 1]);

  return (
    <section className="relative pt-32 pb-0 white-texture" style={{ background: '#0a0a0a' }} data-testid="lumina-hero">
      <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-[680px] mx-auto px-4 text-center">
        <motion.p variants={fadeUp} className="text-sm font-medium tracking-[0.1em] uppercase" style={{ color: '#888' }}>Clinical Intelligence</motion.p>
        <motion.h1 variants={fadeUpHeading} className="text-[40px] md:text-[56px] lg:text-[72px] text-white font-bold leading-[1.1] tracking-[-0.03em] mt-4">
          The note is done before you leave the room.
        </motion.h1>
        <motion.p variants={fadeUp} className="text-lg text-[#aaaaaa] leading-[1.6] max-w-[560px] mx-auto mt-4 font-normal">
          Lumina listens to every patient conversation, understands the clinical context, and delivers a complete SOAP note — automatically. No retyping. No after-hours charting.
        </motion.p>
        <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mt-8">
          <Link to="/integrations" className="h-11 px-6 rounded-lg text-black text-sm font-medium flex items-center gap-2" style={{ background: '#ffffff' }} data-testid="lumina-cta-trial">
            Start free trial
          </Link>
          <button className="h-11 px-6 rounded-lg text-white text-sm font-medium flex items-center gap-2" style={{ border: '0.5px solid #2a2a2a' }} data-testid="lumina-cta-demo">
            See it in action
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll-driven browser mockup */}
      <div className="relative mt-14 flex justify-center" style={{ perspective: '1200px' }}>
        <motion.div
          ref={mockupRef}
          style={{ y, rotateX: rotX, scale: sc, opacity: op, willChange: 'transform, opacity' }}
          className="w-[88vw] max-w-[1080px]"
        >
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
            {/* Chrome bar */}
            <div className="h-10 flex items-center px-3.5" style={{ background: '#242424', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full" style={{ background: '#ff5f57' }} />
                <div className="w-3 h-3 rounded-full" style={{ background: '#febc2e' }} />
                <div className="w-3 h-3 rounded-full" style={{ background: '#28c840' }} />
              </div>
              <div className="mx-auto h-[26px] px-4 rounded-md flex items-center" style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.08)' }}>
                <span className="text-[#555] text-xs font-mono">app.ariaone.ai/lumina</span>
              </div>
            </div>
            <img src={SS.hero} alt="Lumina Clinical Focus" className="w-full object-cover object-top" style={{ height: 580 }} />
          </div>
          {/* Ambient glow */}
          <div className="absolute bottom-[-20px] left-1/2 -translate-x-1/2 w-[60%] h-[80px] rounded-full" style={{ background: 'rgba(107,92,231,0.1)', filter: 'blur(60px)' }} />
        </motion.div>
      </div>
    </section>
  );
};

/* ━━━ SECTION 2: Stat Band ━━━ */
const stats = [
  { num: '70%', label: 'less documentation time' },
  { num: '60+', label: 'languages supported' },
  { num: '4 min', label: 'average visit prep time saved' },
  { num: '0', label: 'after-hours notes with Lumina' },
];

const StatBand = () => (
  <section className="w-full py-10" style={{ background: '#111111', borderTop: '0.5px solid #2a2a2a', borderBottom: '0.5px solid #2a2a2a' }} data-testid="lumina-stat-band">
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[1080px] mx-auto px-4 flex items-center justify-between">
      {stats.map((s, i) => (
        <div key={i} className="flex items-center">
          {i > 0 && <div className="h-10 mr-8 lg:mr-12" style={{ width: '0.5px', background: '#2a2a2a' }} />}
          <motion.div variants={fadeUp} className="text-center flex-1">
            <div className="text-white text-3xl md:text-[40px] font-bold">{s.num}</div>
            <div className="text-[#555] text-xs mt-1">{s.label}</div>
          </motion.div>
        </div>
      ))}
    </motion.div>
  </section>
);

/* ━━━ SECTION 3: Highlight Carousel ━━━ */
const HighlightCarousel = () => {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);

  const advance = useCallback(() => setActive(p => (p + 1) % slides.length), []);

  useEffect(() => {
    if (!playing) return;
    timerRef.current = setInterval(advance, 5000);
    return () => clearInterval(timerRef.current);
  }, [playing, advance]);

  return (
    <section className="py-20 md:py-28 white-texture" style={{ background: '#0a0a0a' }} data-testid="lumina-carousel">
      <div className="max-w-[1200px] mx-auto px-4">
        <h2 className="text-[32px] text-white font-bold mb-8">Get the highlights</h2>
      </div>
      <div className="flex justify-center">
        <div
          className="w-[88vw] max-w-[1200px] rounded-3xl overflow-hidden relative"
          style={{ background: '#111111', height: 520 }}
          onMouseEnter={() => setPlaying(false)}
          onMouseLeave={() => setPlaying(true)}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="absolute inset-0 flex"
            >
              {/* Left text */}
              <div className="w-[38%] p-12 flex flex-col justify-center">
                <p className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>{slides[active].eyebrow}</p>
                <h3 className="text-[28px] text-white font-bold leading-[1.2] mt-3">{slides[active].title}</h3>
                <p className="text-[#aaa] text-base leading-[1.7] mt-3 max-w-[300px] font-normal">{slides[active].body}</p>
              </div>
              {/* Right image */}
              <div className="w-[62%] relative">
                <img src={slides[active].img} alt={slides[active].title} className="absolute inset-0 w-full h-full object-cover object-top" />
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Controls */}
          <div className="absolute bottom-5 left-12 flex items-center gap-3 z-10">
            <button onClick={() => setPlaying(p => !p)} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.08)' }} data-testid="carousel-play-pause">
              {playing ? <Pause className="w-3.5 h-3.5 text-white" /> : <Play className="w-3.5 h-3.5 text-white" />}
            </button>
            <div className="flex gap-1.5">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setActive(i)} className="h-1 rounded-full overflow-hidden" style={{ width: 40, background: 'rgba(255,255,255,0.1)' }} data-testid={`carousel-indicator-${i}`}>
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: '#ffffff' }}
                    initial={{ width: 0 }}
                    animate={{ width: i === active && playing ? '100%' : i === active ? '100%' : '0%' }}
                    transition={{ duration: i === active && playing ? 5 : 0.2, ease: 'linear' }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ━━━ SECTION 4: Sticky Scroll ━━━ */
const StickyScrollSection = () => {
  const [activeBlock, setActiveBlock] = useState(0);
  const blockRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.idx);
            setActiveBlock(idx);
          }
        });
      },
      { threshold: 0.4 }
    );
    blockRefs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="white-texture" style={{ background: '#0a0a0a' }} data-testid="lumina-sticky-scroll">
      {/* Intro */}
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[640px] mx-auto text-center px-4 pb-20">
        <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>How it works</motion.p>
        <motion.h2 variants={fadeUpHeading} className="text-3xl md:text-[48px] text-white font-bold tracking-tight leading-[1.15] mt-4">From schedule to signed note — without touching a keyboard.</motion.h2>
        <motion.p variants={fadeUp} className="text-[17px] text-[#aaa] mt-4 font-normal">Lumina runs in the background of your entire clinical day. Here's what that looks like.</motion.p>
      </motion.div>

      {/* Sticky zone */}
      <div className="relative" style={{ minHeight: '500vh' }}>
        <div className="flex">
          {/* Left scrolling column */}
          <div className="w-[45%] px-8 md:px-14">
            {stickyBlocks.map((block, i) => (
              <div
                key={i}
                ref={el => (blockRefs.current[i] = el)}
                data-idx={i}
                className="py-20 md:py-28"
                style={{ minHeight: '100vh' }}
              >
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
                  <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>{block.eyebrow}</motion.p>
                  <motion.h3 variants={fadeUpHeading} className="text-2xl md:text-3xl text-white font-bold tracking-tight mt-3">{block.h3}</motion.h3>
                  <motion.p variants={fadeUp} className="text-[17px] text-[#aaa] leading-[1.7] mt-4 max-w-[420px] font-normal">{block.body}</motion.p>
                  <motion.div variants={fadeUp} className="inline-block mt-4 px-3.5 py-1 rounded-full text-[13px]" style={{ background: 'rgba(255,255,255,0.06)', border: '0.5px solid rgba(255,255,255,0.15)', color: '#aaa' }}>
                    {block.pill}
                  </motion.div>
                </motion.div>
              </div>
            ))}
          </div>

          {/* Right sticky column */}
          <div className="w-[55%] sticky top-0 h-screen flex items-center p-10">
            <div className="w-full rounded-2xl overflow-hidden" style={{ background: '#111111', border: '0.5px solid #2a2a2a', height: 'calc(100vh - 80px)' }}>
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeBlock}
                  src={stickyBlocks[activeBlock].img}
                  alt={stickyBlocks[activeBlock].h3}
                  className="w-full h-full object-cover object-top rounded-2xl"
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                />
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ━━━ SECTION 5: Full-Width Image Break ━━━ */
const FullWidthBreak = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '10%']);

  return (
    <section ref={ref} className="relative w-full overflow-hidden" style={{ height: '60vh' }} data-testid="lumina-fullwidth-break">
      <motion.img
        src={SS.clinical}
        alt="Lumina patient overview"
        className="absolute inset-0 w-full h-[120%] object-cover object-top"
        style={{ y: imgY }}
      />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 65%, #0a0a0a 100%)' }} />
    </section>
  );
};

/* ━━━ SECTION 6: Lumina Lens Editorial Split ━━━ */
const LensEditorialSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '8%']);

  const checks = [
    'Patient ID cards and insurance documents',
    'Lab results, radiology reports, fax PDFs',
    'Appointment schedules and discharge summaries',
  ];

  return (
    <section ref={ref} className="w-full" style={{ background: '#0a0a0a' }} data-testid="lumina-lens-editorial">
      <div className="flex flex-col lg:flex-row min-h-[600px]">
        {/* Left text */}
        <motion.div
          className="w-full lg:w-1/2 px-8 md:px-16 lg:px-20 flex flex-col justify-center py-16"
          initial={{ opacity: 0, x: -32 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>Lumina Lens</p>
          <h2 className="text-3xl md:text-[48px] text-white font-bold leading-[1.15] tracking-tight max-w-[460px] mt-4">Upload anything. Lens reads it.</h2>
          <p className="text-[17px] text-[#aaa] leading-[1.7] max-w-[420px] mt-4 font-normal">
            Photo a referral letter, scan a lab result, upload a CSV — Lens extracts the clinical data and adds it directly to the SOAP note. No manual entry. No context switching.
          </p>
          <div className="mt-5 space-y-2.5">
            {checks.map((c, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <Check className="w-4 h-4 flex-shrink-0" style={{ color: '#888' }} />
                <span className="text-[#aaa] text-sm font-normal">{c}</span>
              </div>
            ))}
          </div>
          <Link to="/integrations" className="text-white text-[15px] mt-6 inline-flex items-center gap-1.5 hover:underline">
            See Lens in action <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
        {/* Right image */}
        <motion.div className="w-full lg:w-1/2 relative overflow-hidden min-h-[400px]" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <motion.img src={SS.encounterReview} alt="Lumina Lens" className="absolute inset-0 w-full h-full object-cover object-top" style={{ y: imgY }} />
        </motion.div>
      </div>
    </section>
  );
};

/* ━━━ SECTION 7: Feature Card Grid ━━━ */
const FeatureGrid = () => (
  <section className="py-20 md:py-28 white-texture" style={{ background: '#111111' }} data-testid="lumina-feature-grid">
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[1200px] mx-auto px-4">
      <motion.div variants={fadeUp} className="text-center mb-14">
        <p className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>Everything in Lumina</p>
        <h2 className="text-3xl md:text-[48px] text-white font-bold tracking-tight mt-3">Built for the full clinical day.</h2>
        <p className="text-[#aaa] text-base mt-3 font-normal">From the morning dashboard to the last signed note.</p>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {featureCards.map((card, i) => (
          <motion.div
            key={i}
            variants={fadeUp}
            className="rounded-2xl p-7 transition-colors duration-200 group"
            style={{ background: '#111111', border: '0.5px solid #2a2a2a' }}
            whileHover={{ borderColor: 'rgba(255,255,255,0.3)' }}
            data-testid={`feature-card-${i}`}
          >
            <div className="h-20 rounded-lg overflow-hidden mb-4" style={{ background: '#1a1a1a' }}>
              <img src={card.img} alt={card.name} className="w-full h-full object-cover object-top" />
            </div>
            <h3 className="text-white text-lg font-bold mt-2">{card.name}</h3>
            <p className="text-[#aaa] text-sm leading-[1.6] mt-2 font-normal">{card.desc}</p>
          </motion.div>
        ))}
      </div>
    </motion.div>
  </section>
);

/* ━━━ SECTION 8: Editorial Flipped (MIPS) ━━━ */
const MipsEditorialSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '8%']);

  return (
    <section ref={ref} className="w-full" style={{ background: '#0a0a0a' }} data-testid="lumina-mips-editorial">
      <div className="flex flex-col lg:flex-row min-h-[600px]">
        {/* Left image */}
        <motion.div className="w-full lg:w-1/2 relative overflow-hidden min-h-[400px] order-2 lg:order-1" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <motion.img src={SS.mips} alt="MIPS Dashboard" className="absolute inset-0 w-full h-full object-cover object-top" style={{ y: imgY }} />
        </motion.div>
        {/* Right text */}
        <motion.div
          className="w-full lg:w-1/2 px-8 md:px-16 lg:px-20 flex flex-col justify-center py-16 order-1 lg:order-2"
          initial={{ opacity: 0, x: 32 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>MIPS & Quality</p>
          <h2 className="text-3xl md:text-[48px] text-white font-bold leading-[1.15] tracking-tight max-w-[460px] mt-4">Track every measure automatically.</h2>
          <p className="text-[17px] text-[#aaa] leading-[1.7] max-w-[420px] mt-4 font-normal">
            Lumina monitors your MIPS performance in real time — quality measures, improvement activities, and promoting interoperability. Know your projected score before the year ends.
          </p>
          <Link to="/integrations" className="text-white text-[15px] mt-6 inline-flex items-center gap-1.5 hover:underline">
            Explore MIPS Dashboard <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

/* ━━━ SECTION 9: Pricing ━━━ */
const PricingSection = () => (
  <section className="py-20 md:py-28 white-texture" style={{ background: '#111111', borderTop: '0.5px solid #2a2a2a' }} data-testid="lumina-pricing">
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[900px] mx-auto px-4 text-center">
      <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>Flexible Usage Model</motion.p>
      <motion.h2 variants={fadeUpHeading} className="text-2xl md:text-3xl text-white font-bold tracking-tight mt-3">Pay only for what you use</motion.h2>
      <motion.p variants={fadeUp} className="text-[#aaa] text-base mt-3 max-w-[500px] mx-auto font-normal">
        Start with one product or deploy the full platform. No long-term contracts required.
      </motion.p>

      <motion.div variants={fadeUp} className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
        {[
          { name: 'Lumina', credits: '50–100', desc: 'Per encounter. Includes ambient capture, SOAP note, and coding review.' },
          { name: 'RevQ', credits: '30–80', desc: 'Per claim. AI scrubbing, submission, denial management.' },
          { name: 'Echo', credits: '20–50', desc: 'Per scheduling action. Smart scheduling, AI phone, patient flow.' },
        ].map((p, i) => (
          <div key={i} className="rounded-xl p-6" style={{ background: '#1a1a1a', border: '0.5px solid #2a2a2a' }}>
            <div className="flex items-center gap-2 mb-3">
              <img src={`/screenshots/${p.name.toLowerCase()}_emblem_white.png`} alt={p.name} className="w-6 h-6 object-contain" />
              <span className="text-white text-base font-bold">{p.name}</span>
            </div>
            <div className="text-2xl text-white font-bold">{p.credits} <span className="text-sm font-normal text-[#555]">credits</span></div>
            <p className="text-[#aaa] text-sm mt-2 font-normal leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </motion.div>

      <motion.div variants={fadeUp} className="mt-8 rounded-xl p-6" style={{ background: '#1a1a1a', border: '0.5px solid #2a2a2a' }}>
        <h3 className="text-xl text-white font-bold mb-1">High-Volume Subscription</h3>
        <p className="text-[#aaa] text-sm font-normal">Enterprise pricing available for practices processing 500+ encounters/month. Dedicated onboarding included.</p>
      </motion.div>
    </motion.div>
  </section>
);

/* ━━━ SECTION 10: Ecosystem Band ━━━ */
const EcosystemBand = () => (
  <section className="py-14" style={{ background: '#0a0a0a', borderTop: '0.5px solid #2a2a2a' }} data-testid="lumina-ecosystem">
    <div className="max-w-[600px] mx-auto px-4 text-center">
      <p className="text-[#555] text-xs uppercase tracking-[0.08em]">Part of the Aria One platform</p>
      <div className="flex items-center justify-center gap-12 mt-5">
        {['lumina', 'revq', 'echo'].map(name => (
          <img key={name} src={`/screenshots/${name}_logo_white.png`} alt={name} className="h-6 object-contain" style={{ opacity: 0.6 }} />
        ))}
      </div>
      <p className="text-[#555] text-sm mt-4 max-w-[500px] mx-auto font-normal">
        Lumina notes flow directly into RevQ for billing. Echo schedules the follow-up. One patient record, three intelligent systems.
      </p>
    </div>
  </section>
);

/* ━━━ SECTION 11: Final CTA ━━━ */
const FinalCTA = () => (
  <section className="py-24 white-texture" style={{ background: '#111111', borderTop: '0.5px solid #2a2a2a' }} data-testid="lumina-final-cta">
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[640px] mx-auto px-4 text-center">
      <motion.h2 variants={fadeUpHeading} className="text-3xl md:text-[40px] text-white font-bold tracking-tight">Ready to stop charting after hours?</motion.h2>
      <motion.p variants={fadeUp} className="text-[#aaa] text-base mt-3 font-normal">Start with Lumina Basic today. No long-term contract. Cancel anytime.</motion.p>
      <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mt-8">
        <Link to="/integrations" className="h-11 px-6 rounded-lg text-black text-sm font-medium flex items-center" style={{ background: '#ffffff' }}>
          Start free trial
        </Link>
        <button className="h-11 px-6 rounded-lg text-white text-sm font-medium flex items-center" style={{ border: '0.5px solid #2a2a2a' }}>
          Request a demo
        </button>
      </motion.div>
    </motion.div>
  </section>
);

/* ━━━ Page Assembly ━━━ */
const LuminaPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ background: '#0a0a0a' }}>
      <HeroSection />
      <StatBand />
      <HighlightCarousel />
      <StickyScrollSection />
      <FullWidthBreak />
      <LensEditorialSection />
      <FeatureGrid />
      <MipsEditorialSection />
      <PricingSection />
      <EcosystemBand />
      <FinalCTA />
    </div>
  );
};

export default LuminaPage;
